import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { defineMessages, injectIntl } from 'react-intl';
import { compose } from 'redux';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import MuiCircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableFooter from '@mui/material/TableFooter';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '../../../../components/CircularProgress';
import EnhancedTableHead from '../../../../components/EnhancedTableHead/EnhancedTableHead';
import ToolbarContainer from '../../../../components/statelessComponents/TableToolbar/Container';
import { ToolbarSelectField } from '../../../../components/statelessComponents/TableToolbar/Forms';
import { ResponsiveTableBody, TablePagination, } from '../../../../components/statelessComponents/Table';
import APPROVAL_STATUSES from '../../../../constants/approvalStepStatuses';
import { isSupportAdminAuth } from '../../../../helpers';
import s from './PendingApprovalsTable.scss';
const messages = defineMessages({
    viewPendingApprovals: {
        id: 'CourseDetailsManager.viewPendingApprovals',
        defaultMessage: 'View pending approvals',
    },
    user: {
        id: 'CourseDetailsManager.user',
        defaultMessage: 'User',
    },
    step: {
        id: 'CourseDetailsManager.step',
        defaultMessage: 'Step',
    },
    submitted: {
        id: 'CourseDetailsManager.submitted',
        defaultMessage: 'Submitted',
    },
    action: {
        id: 'CourseDetailsManager.action',
        defaultMessage: 'Action',
    },
    approve: {
        id: 'CourseDetailsManager.approve',
        defaultMessage: 'Approve',
    },
    reject: {
        id: 'CourseDetailsManager.reject',
        defaultMessage: 'Reject',
    },
    noPendingApprovals: {
        id: 'CourseDetailsManager.noPendingApprovals',
        defaultMessage: 'No approval steps are waiting for review.',
    },
    statusFilter: {
        id: 'CourseDetailsManager.statusFilter',
        defaultMessage: 'Status',
    },
    waitingForReview: {
        id: 'CourseDetailsManager.waitingForReview',
        defaultMessage: 'Waiting for review',
    },
    approved: {
        id: 'CourseDetailsManager.approved',
        defaultMessage: 'Approved',
    },
    rejected: {
        id: 'CourseDetailsManager.rejected',
        defaultMessage: 'Rejected',
    },
    userEmail: {
        id: 'CourseDetailsManager.userEmail',
        defaultMessage: 'Email: {email}',
    },
    mainGroup: {
        id: 'CourseDetailsManager.mainGroup',
        defaultMessage: 'Main group: {mainGroup}',
    },
    confirmReviewTitle: {
        id: 'CourseDetailsManager.confirmReviewTitle',
        defaultMessage: 'Confirm review',
    },
    confirmApprove: {
        id: 'CourseDetailsManager.confirmApprove',
        defaultMessage: 'Approve this approval step for {userName}?',
    },
    confirmReject: {
        id: 'CourseDetailsManager.confirmReject',
        defaultMessage: 'Reject this approval step for {userName}?',
    },
    cancel: {
        id: 'CourseDetailsManager.cancel',
        defaultMessage: 'Cancel',
    },
    confirm: {
        id: 'CourseDetailsManager.confirm',
        defaultMessage: 'Confirm',
    },
});
const statusFilterOptions = [
    {
        value: APPROVAL_STATUSES.WAITING_FOR_REVIEW,
        message: messages.waitingForReview,
    },
    {
        value: APPROVAL_STATUSES.APPROVED,
        message: messages.approved,
    },
    {
        value: APPROVAL_STATUSES.REJECTED,
        message: messages.rejected,
    },
];
function PendingApprovalsTable({ auth, intl: { formatMessage }, loading, onChangePage, onReviewApproval, page, pendingApprovals, perPage, statusFilter, total, updatingApprovalId, onStatusFilterChange, }) {
    const [reviewToConfirm, setReviewToConfirm] = React.useState(null);
    const AnyResponsiveTableBody = ResponsiveTableBody;
    const AnyTablePagination = TablePagination;
    const isWaitingForReview = statusFilter === APPROVAL_STATUSES.WAITING_FOR_REVIEW;
    const isSupportAdmin = isSupportAdminAuth(auth);
    const getUserName = (approval) => [
        _.get(approval, 'user.firstName', ''),
        _.get(approval, 'user.lastName', ''),
    ]
        .filter(Boolean)
        .join(' ') ||
        _.get(approval, 'user.fullName') ||
        _.get(approval, 'user.email') ||
        '-';
    const getUserMainGroupName = (approval) => _.get(approval, 'user.mainGroup.name') ||
        _.get(approval, 'user.mainGroupId.name');
    const getUserTooltipRows = (approval) => [
        approval.user?.email &&
            formatMessage(messages.userEmail, { email: approval.user.email }),
        getUserMainGroupName(approval) &&
            formatMessage(messages.mainGroup, {
                mainGroup: getUserMainGroupName(approval),
            }),
    ].filter(Boolean);
    const closeReviewConfirmation = () => setReviewToConfirm(null);
    const confirmReview = () => {
        if (!reviewToConfirm) {
            return;
        }
        onReviewApproval(reviewToConfirm.approval, reviewToConfirm.status);
        closeReviewConfirmation();
    };
    const columnData = [
        {
            id: 'user',
            numeric: false,
            disablePadding: false,
            disableSorting: true,
            label: formatMessage(messages.user),
        },
        {
            id: 'approvalStep',
            numeric: false,
            disablePadding: false,
            disableSorting: true,
            label: formatMessage(messages.step),
        },
        {
            id: 'createDate',
            numeric: false,
            disablePadding: false,
            disableSorting: true,
            label: formatMessage(messages.submitted),
            cellClassName: s.dateCell,
        },
        isWaitingForReview && {
            id: 'action',
            numeric: true,
            disablePadding: false,
            disableSorting: true,
            label: formatMessage(messages.action),
            cellClassName: s.actionCell,
        },
    ].filter(Boolean);
    return (React.createElement(Paper, { className: s.root },
        React.createElement(ToolbarContainer, { styled: true, loading: loading },
            React.createElement("div", { className: s.toolbar },
                React.createElement("div", { className: s.toolbarActions },
                    React.createElement(ToolbarSelectField, { "data-test-id": "pending-approvals-status-filter", className: s.statusFilter, id: "pending-approvals-status-filter", label: formatMessage(messages.statusFilter), value: statusFilter, onChange: (event) => onStatusFilterChange(event.target.value) }, statusFilterOptions.map(option => (React.createElement(MenuItem, { key: option.value, value: option.value }, formatMessage(option.message)))))))),
        React.createElement("div", { className: s.tableWrapper },
            loading && (React.createElement("div", { className: s.loadingWrapper },
                React.createElement(CircularProgress, { size: 100 }))),
            React.createElement(Table, { className: classNames(s.table, {
                    [s.loading]: loading,
                }) },
                React.createElement(EnhancedTableHead, { order: "asc", orderBy: "createDate", onRequestSort: () => null, rowCount: total, loading: loading, columnData: columnData }),
                React.createElement(AnyResponsiveTableBody, { isEmpty: !loading && _.isEmpty(pendingApprovals), emptyMessage: messages.noPendingApprovals }, pendingApprovals.map(approval => {
                    const isUpdating = updatingApprovalId === approval._id;
                    const userName = getUserName(approval);
                    const userTooltipRows = getUserTooltipRows(approval);
                    return (React.createElement(TableRow, { key: approval._id, hover: true, className: s.row },
                        React.createElement(TableCell, null, isSupportAdmin && userTooltipRows.length ? (React.createElement(Tooltip, { title: React.createElement(React.Fragment, null, userTooltipRows.map(row => (React.createElement("div", { key: row }, row)))) },
                            React.createElement("span", null, userName))) : (userName)),
                        React.createElement(TableCell, null, approval.approvalStep.name),
                        React.createElement(TableCell, { className: s.dateCell }, approval.createDate
                            ? moment(approval.createDate).format('YYYY-MM-DD HH:mm')
                            : '-'),
                        isWaitingForReview && (React.createElement(TableCell, { align: "right", className: s.actionCell },
                            React.createElement(Button, { color: "primary", disabled: !!updatingApprovalId, onClick: () => setReviewToConfirm({
                                    approval,
                                    status: APPROVAL_STATUSES.APPROVED,
                                }), size: "small", variant: "contained" },
                                isUpdating && (React.createElement(MuiCircularProgress, { color: "inherit", size: 16, className: s.buttonProgress })),
                                formatMessage(messages.approve)),
                            React.createElement(Button, { color: "secondary", disabled: !!updatingApprovalId, onClick: () => setReviewToConfirm({
                                    approval,
                                    status: APPROVAL_STATUSES.REJECTED,
                                }), size: "small", className: s.rejectButton }, formatMessage(messages.reject))))));
                })),
                React.createElement(TableFooter, null,
                    React.createElement(TableRow, null,
                        React.createElement(AnyTablePagination, { colSpan: isWaitingForReview ? 4 : 3, count: total, rowsPerPage: perPage, page: page, rowsPerPageOptions: [perPage], onChangePage: onChangePage, classes: {
                                caption: s.pagination,
                            } }))))),
        React.createElement(Dialog, { open: !!reviewToConfirm, onClose: closeReviewConfirmation },
            React.createElement(DialogTitle, null, formatMessage(messages.confirmReviewTitle)),
            React.createElement(DialogContent, null,
                React.createElement(DialogContentText, null, reviewToConfirm &&
                    (reviewToConfirm.status === APPROVAL_STATUSES.APPROVED
                        ? formatMessage(messages.confirmApprove, {
                            userName: getUserName(reviewToConfirm.approval),
                        })
                        : formatMessage(messages.confirmReject, {
                            userName: getUserName(reviewToConfirm.approval),
                        })))),
            React.createElement(DialogActions, null,
                React.createElement(Button, { "data-test-id": "pending-approvals-cancel-review", color: "primary", onClick: closeReviewConfirmation }, formatMessage(messages.cancel)),
                React.createElement(Button, { "data-test-id": "pending-approvals-confirm-review", autoFocus: true, color: "primary", disabled: !!updatingApprovalId, onClick: confirmReview, variant: "contained" }, formatMessage(messages.confirm))))));
}
export default compose(injectIntl, withStyles(s))(PendingApprovalsTable);
